import { deepEqual } from '@shared/utils/deepEqual';

import {
  IEditorDiff,
  IEditorPolicy,
  IEditorPolicyCondition,
  IEditorPolicyInput,
  IEditorRule,
  IEditorRuleInput,
  IRoutingConfigPolicy,
  IRoutingConfigPolicyCondition,
  IRoutingConfigPolicyPayload,
  IRoutingConfigPutBody,
  IRoutingConfigRuleParam,
  IRoutingConfigRulePayload,
  TRoutingConfigRuleParamValue,
} from '../model/types';

const byPriority = <T extends { priority: number }>(a: T, b: T) => a.priority - b.priority;

// The API emits currency/method/traffic as an array or a bare string.
const toArray = (value?: string[] | string): string[] =>
  Array.isArray(value) ? [...value] : value ? [value] : [];

// Array-only conditions: an untouched policy round-trips the form without diffing.
const normalizeCondition = (condition: IRoutingConfigPolicyCondition): IEditorPolicyCondition => ({
  currency: toArray(condition?.currency),
  method: toArray(condition?.method),
  traffic: toArray(condition?.traffic),
  merchants: toArray(condition?.merchants),
});

// GET → editor: priority order becomes array order, every node gets a client uid.
export const toEditorPolicies = (policies: IRoutingConfigPolicy[]): IEditorPolicy[] =>
  [...(policies ?? [])].sort(byPriority).map(policy => ({
    ...policy,
    uid: String(policy.id),
    conditions: (policy.conditions ?? []).map(normalizeCondition),
    rules: [...(policy.rules ?? [])]
      .sort(byPriority)
      .map<IEditorRule>(rule => ({ ...rule, uid: String(rule.id) })),
  }));

// Client ids for nodes the server has not seen yet.
const newUid = () => crypto.randomUUID();

// Editor data is JSON-safe (it lives in localStorage), so a JSON copy is enough.
const deepCopy = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

// New policies start switched off and without rules.
export const createEditorPolicy = (input: IEditorPolicyInput): IEditorPolicy => ({
  uid: newUid(),
  name: input.name,
  description: input.description,
  active: false,
  conditions: deepCopy(input.conditions),
  priority: 0,
  priority_auto: null,
  rules: [],
});

// New rules start active (as the legacy rule form creates them) and land last in
// their policy, so they only catch what the rules above them let through.
export const createEditorRule = (input: IEditorRuleInput): IEditorRule => ({
  uid: newUid(),
  name: input.name,
  description: input.description,
  active: true,
  min: input.min,
  max: input.max,
  conditions: deepCopy(input.conditions),
  selections: deepCopy(input.selections),
  sort_strategy: input.sort_strategy,
  priority: 0,
  priority_auto: null,
});

// Fresh uids, no server ids, switched off — like the legacy clone.
export const cloneEditorPolicy = (policy: IEditorPolicy): IEditorPolicy => {
  const { id: _id, rules, ...rest } = deepCopy(policy);

  return {
    ...rest,
    uid: newUid(),
    name: `${policy.name} (copy)`,
    active: false,
    rules: rules.map(({ id: _ruleId, ...rule }) => ({ ...rule, uid: newUid() })),
  };
};

// GET gives rule params as [{name, rawValue, …}]; PUT wants {techname: rawValue}.
const toParamsPayload = (
  params: IRoutingConfigRuleParam[],
): Record<string, TRoutingConfigRuleParamValue> =>
  (params ?? []).reduce<Record<string, TRoutingConfigRuleParamValue>>((acc, param) => {
    acc[param.name] = param.rawValue;
    return acc;
  }, {});

const toRulePayload = (rule: IEditorRule, priority: number): IRoutingConfigRulePayload => ({
  name: rule.name,
  description: rule.description ?? null,
  active: rule.active,
  min: Number(rule.min),
  max: Number(rule.max),
  conditions: toParamsPayload(rule.conditions),
  selections: toParamsPayload(rule.selections),
  sort_strategy: rule.sort_strategy,
  priority,
});

const toPolicyPayload = (policy: IEditorPolicy, priority: number): IRoutingConfigPolicyPayload => ({
  name: policy.name,
  description: policy.description ?? null,
  active: policy.active,
  conditions: policy.conditions ?? [],
  priority,
  rules: policy.rules.map((rule, index) => toRulePayload(rule, index + 1)),
});

// Editor → PUT: ids and uids are stripped, priorities are positional.
export const toPutPayload = (policies: IEditorPolicy[]): IRoutingConfigPutBody => ({
  policies: policies.map((policy, index) => toPolicyPayload(policy, index + 1)),
});

// Comparable shapes for change tracking: the PUT shape minus position (position
// is diffed separately).
const normalizePolicy = (policy: IEditorPolicy) => {
  const { priority: _priority, ...rest } = toPolicyPayload(policy, 0);
  return rest;
};

const normalizeRule = (rule: IEditorRule) => {
  const { priority: _priority, ...rest } = toRulePayload(rule, 0);
  return rest;
};

// Uids whose position among the survivors changed — additions and removals alone
// move nobody.
const movedUids = <T extends { uid: string }>(original: T[], draft: T[]): Set<string> => {
  const originalUids = new Set(original.map(item => item.uid));
  const draftUids = new Set(draft.map(item => item.uid));
  const before = original.filter(item => draftUids.has(item.uid)).map(item => item.uid);
  const after = draft.filter(item => originalUids.has(item.uid)).map(item => item.uid);

  return new Set(after.filter((uid, index) => before[index] !== uid));
};

// Rule marks inside one surviving policy: added, or edited / moved among the survivors.
const diffRules = (original: IEditorRule[], draft: IEditorRule[]) => {
  const originalByUid = new Map(original.map(rule => [rule.uid, rule]));
  const moved = movedUids(original, draft);
  const changedUids: string[] = [];
  const addedUids: string[] = [];

  draft.forEach(rule => {
    const before = originalByUid.get(rule.uid);

    if (!before) {
      changedUids.push(rule.uid);
      addedUids.push(rule.uid);
    } else if (moved.has(rule.uid) || !deepEqual(normalizeRule(before), normalizeRule(rule))) {
      changedUids.push(rule.uid);
    }
  });

  return { changedUids, addedUids };
};

const policyLabel = (policy: IEditorPolicy, index: number) => policy.name || `#${index + 1}`;

// One change per policy (edited / moved / added / removed). `changedUids` marks
// rows; `summary` is the banner's one-liner; the rule uids mark the nested rows.
export const diffPolicies = (original: IEditorPolicy[], draft: IEditorPolicy[]): IEditorDiff => {
  const originalByUid = new Map(original.map(policy => [policy.uid, policy]));
  const draftUids = new Set(draft.map(policy => policy.uid));
  const moved = movedUids(original, draft);

  const changedUids: string[] = [];
  const addedUids: string[] = [];
  const changedRuleUids: string[] = [];
  const addedRuleUids: string[] = [];
  const summary: string[] = [];

  draft.forEach((policy, index) => {
    const before = originalByUid.get(policy.uid);
    const label = policyLabel(policy, index);

    if (!before) {
      changedUids.push(policy.uid);
      addedUids.push(policy.uid);
      summary.push(`${label} → added`);
      return;
    }

    const rules = diffRules(before.rules, policy.rules);
    changedRuleUids.push(...rules.changedUids);
    addedRuleUids.push(...rules.addedUids);

    const edited = !deepEqual(normalizePolicy(before), normalizePolicy(policy));
    if (!moved.has(policy.uid) && !edited) return;

    changedUids.push(policy.uid);
    // A bare On/Off flip reads better as its outcome.
    const onlyToggled =
      edited &&
      before.active !== policy.active &&
      deepEqual(normalizePolicy({ ...before, active: policy.active }), normalizePolicy(policy));
    const verb = onlyToggled ? (policy.active ? 'on' : 'off') : edited ? 'edited' : 'moved';
    summary.push(`${label} → ${verb}`);
  });

  let removedCount = 0;
  original.forEach((policy, index) => {
    if (draftUids.has(policy.uid)) return;
    removedCount += 1;
    summary.push(`${policyLabel(policy, index)} → removed`);
  });

  const count = changedUids.length + removedCount;

  return {
    count,
    changedUids,
    addedUids,
    changedRuleUids,
    addedRuleUids,
    isDirty: count > 0,
    summary,
  };
};
